import type {KeyValue, WritableKeyValue} from '@jetbrains/teamcity-ui/dist/utils'
import deepEqual from 'fast-deep-equal'
import isEmpty from 'lodash/isEmpty'
import type {Reducer} from 'redux'
import {combineReducers} from 'redux'

import {feedbackForm} from './components/Feedback/Feedback.slice'
import {
  collapsedBlocks,
  debug,
  hoveredJob,
  jobDependencyGuide,
  pipelineDependencyDetails,
  pipelineDraft,
  pipelineDraftForm,
  pipelineErrors,
  pipelineForm,
  pipelineSecrets,
  pipelineStepSecrets,
  pipelineYaml,
  pipelineYamlValidity,
  pipelineKotlinDsl,
  suggestions,
} from './EditPipelinePage/slices/EditPipelinePage.slices'
import {
  getEffectivePipelineBranch,
  getPipelineDraftEntry,
  getYamlDraftEntry,
  isDraftChanged,
} from './EditPipelinePage/slices/EditPipelinePage.slices.utils'
import {pipelinesApi} from './services/pipelinesApi'

import type {PipelineId} from 'src/types'

const pipelinesCombined = combineReducers({
  collapsedBlocks: collapsedBlocks.reducer,
  debug: debug.reducer,
  hoveredJob: hoveredJob.reducer,
  jobDependencyGuide: jobDependencyGuide.reducer,
  pipelineDraft: pipelineDraft.reducer,
  pipelineDraftForm: pipelineDraftForm.reducer,
  pipelineYamlDraft: pipelineYaml.reducer,
  pipelineYamlValidity: pipelineYamlValidity.reducer,
  pipelineKotlinDsl: pipelineKotlinDsl.reducer,
  pipelineForm: pipelineForm.reducer,
  feedbackForm: feedbackForm.reducer,
  suggestions: suggestions.reducer,
  pipelineErrors: pipelineErrors.reducer,
  pipelineSecrets: pipelineSecrets.reducer,
  pipelineStepSecrets: pipelineStepSecrets.reducer,
  pipelineDependencyDetails: pipelineDependencyDetails.reducer,
})

type PipelinesState = ReturnType<typeof pipelinesCombined>

function isPristineDraft(state: PipelinesState, id: PipelineId, branch: string) {
  const entry = getPipelineDraftEntry(state.pipelineDraft, id, branch)
  const {draft, original, added} = entry ?? {}
  const yamlDraft = getYamlDraftEntry(state.pipelineYamlDraft, id, branch)?.yaml

  return (
    (draft == null || deepEqual(draft, original)) &&
    !isDraftChanged(entry) &&
    isEmpty(added) &&
    (yamlDraft == null || yamlDraft.trim() === (original?.yaml ?? '').trim()) &&
    isEmpty(state.pipelineSecrets[id]?.[branch]) &&
    isEmpty(state.pipelineStepSecrets[id]?.[branch])
  )
}

function withoutBranchEntry<K extends string, T>(
  byPipeline: KeyValue<K, KeyValue<string, T>>,
  id: K,
  branch: string,
): KeyValue<K, KeyValue<string, T>> {
  const byBranch = byPipeline[id]
  if (byBranch?.[branch] == null) {
    return byPipeline
  }
  const restByBranch: WritableKeyValue<string, T> = {...byBranch}
  delete restByBranch[branch]

  const next: WritableKeyValue<K, KeyValue<string, T>> = {...byPipeline}
  next[id] = restByBranch
  return next
}

/**
 * Drops a pristine draft before the draft reducers see the response, so that they re-create it from
 * the new data instead of reporting an external settings change as unsaved changes of the user.
 */
export const pipelines: Reducer<PipelinesState> = (state, action) => {
  if (state != null && pipelinesApi.endpoints.getPipelineById.matchFulfilled(action)) {
    const {id, branch} = action.meta.arg.originalArgs
    // Only the editor sends a branch. Other pages just read the pipeline, and their response can
    // arrive late with older data, so it must not touch the draft.
    if (branch != null) {
      const effectiveBranch = getEffectivePipelineBranch(action.payload, branch)
      const entry = getPipelineDraftEntry(state.pipelineDraft, id, effectiveBranch)
      if (
        entry != null &&
        !deepEqual(entry.original, action.payload) &&
        isPristineDraft(state, id, effectiveBranch)
      ) {
        return pipelinesCombined(
          {
            ...state,
            pipelineDraft: withoutBranchEntry(state.pipelineDraft, id, effectiveBranch),
            pipelineYamlDraft: withoutBranchEntry(state.pipelineYamlDraft, id, effectiveBranch),
          },
          action,
        )
      }
    }
  }

  return pipelinesCombined(state, action)
}
